import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getUserUsageSummary, getUserUsageLogs, UsageLog } from '../api/usage';
import { FREE_REQUESTS_PER_MONTH } from '../config/constants';

interface UsageDataContextType {
  usageData: any;
  recentLogs: UsageLog[];
  currentMonthUsage: {
    requests: number;
    cost: number;
    freeRequestsUsed: number;
    freeRequestsRemaining: number;
    avgCallsPerDay: number;
  };
  loading: boolean;
  logsLoading: boolean;
  dataLoaded: boolean;
  loadUsageData: (startDate?: string, endDate?: string) => Promise<void>;
  loadRecentLogs: (startDate?: string, endDate?: string, limit?: number, offset?: number, append?: boolean) => Promise<UsageLog[]>;
  refreshData: (startDate?: string, endDate?: string) => Promise<void>;
  refreshCurrentMonthData: () => Promise<void>;
}

const UsageDataContext = createContext<UsageDataContextType | undefined>(undefined);

export const useUsageData = () => {
  const context = useContext(UsageDataContext);
  if (context === undefined) {
    throw new Error('useUsageData must be used within a UsageDataProvider');
  }
  return context;
};

interface UsageDataProviderProps {
  children: ReactNode;
}

export const UsageDataProvider: React.FC<UsageDataProviderProps> = ({ children }) => {
  const [usageData, setUsageData] = useState<any>(null);
  const [recentLogs, setRecentLogs] = useState<UsageLog[]>([]);
  const [currentMonthUsage, setCurrentMonthUsage] = useState({
    requests: 0,
    cost: 0,
    freeRequestsUsed: 0,
    freeRequestsRemaining: FREE_REQUESTS_PER_MONTH,
    avgCallsPerDay: 0
  });
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { user } = useAuth();

  const loadUsageData = useCallback(async (startDate?: string, endDate?: string) => {
    if (!user) {
      return;
    }
  
  
    setLoading(true);
    try {
      let usageSummary;
      if (startDate && endDate) {
        // For date ranges, calculate from logs directly for accuracy
        const start = new Date(startDate).toISOString();
        const end = new Date(endDate + 'T23:59:59.999Z').toISOString();
        
        
        // Get logs for the date range and calculate totals
        const logs = await getUserUsageLogs(user.id, 10000, 0, start, end); // Get up to 10k logs
        
        if (!logs || logs.length === 0) {
          setUsageData({ totalRequests: 0, totalCost: 0, successRate: 0, avgResponseTime: 0, avgCallsPerDay: 0 });
          return;
        }
        
        // Calculate totals from logs
        const totalRequests = logs.length;
        const totalCost = logs.reduce((sum, log) => sum + (log.cost_usd || 0), 0);
        const successfulRequests = logs.filter(log => log.status_code >= 200 && log.status_code < 300).length;
        const totalResponseTime = logs.reduce((sum, log) => sum + (log.response_time_ms || 0), 0);
        
        const successRate = totalRequests > 0 ? Math.round((successfulRequests / totalRequests) * 100) : 0;
        const avgResponseTime = totalRequests > 0 ? Math.round(totalResponseTime / totalRequests) : 0;
        
        // Calculate average calls per day for the date range
        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        const timeDiff = endDateObj.getTime() - startDateObj.getTime();
        const daysInPeriod = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
        const avgCallsPerDay = totalRequests / daysInPeriod;
        

        setUsageData({
          totalRequests,
          totalCost,
          successRate,
          avgResponseTime,
          avgCallsPerDay
        });
      } else {
        // Use summary table for all data
        const start = '2025-01-01T00:00:00.000Z';
        const end = '2025-12-31T23:59:59.999Z';
        
        usageSummary = await getUserUsageSummary(user.id, start, end);
        
        
        if (!usageSummary || usageSummary.length === 0) {
          setUsageData({ totalRequests: 0, totalCost: 0, successRate: 0, avgResponseTime: 0, avgCallsPerDay: 0 });
          return;
        }
        
        // Calculate totals using snake_case fields
        const totalRequests = usageSummary.reduce((sum, period) => sum + (period.total_requests || 0), 0);
        const totalCost = usageSummary.reduce((sum, period) => {
          const cost = period.total_cost_usd;
          return sum + (cost ? parseFloat(cost.toString()) : 0);
        }, 0);
        const successfulRequests = usageSummary.reduce((sum, period) => sum + (period.successful_requests || 0), 0);
        const totalResponseTime = usageSummary.reduce((sum, period) => sum + (period.total_response_time_ms || 0), 0);
        
        const successRate = totalRequests > 0 ? Math.round((successfulRequests / totalRequests) * 100) : 0;
        const avgResponseTime = totalRequests > 0 ? Math.round(totalResponseTime / totalRequests) : 0;
        
        // Calculate average calls per day for the year (2025)
        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        const timeDiff = endDateObj.getTime() - startDateObj.getTime();
        const daysInPeriod = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
        const avgCallsPerDay = totalRequests / daysInPeriod;
        

        setUsageData({
          totalRequests,
          totalCost,
          successRate,
          avgResponseTime,
          avgCallsPerDay
        });
      }
    } catch (error) {
      console.error('❌ Failed to load usage data:', error);
      setUsageData({ totalRequests: 0, totalCost: 0, successRate: 0, avgResponseTime: 0, avgCallsPerDay: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadRecentLogs = useCallback(async (startDate?: string, endDate?: string, limit?: number, offset?: number, append?: boolean) => {
    if (!user) {
      return [];
    }
  
    const logsLimit = limit || 10;
    const logsOffset = offset || 0;
    setLogsLoading(true);
    try {
      let logs: UsageLog[];
      if (startDate && endDate) {
        // Convert date inputs to ISO strings for database query
        const start = new Date(startDate).toISOString();
        const end = new Date(endDate + 'T23:59:59.999Z').toISOString();
        
        logs = await getUserUsageLogs(user.id, logsLimit, logsOffset, start, end);
      } else {
        // Get recent logs with specified limit and offset
        logs = await getUserUsageLogs(user.id, logsLimit, logsOffset);
      }
      
      if (append && logs && logs.length > 0) {
        setRecentLogs(prev => [...prev, ...logs]);
      } else {
        setRecentLogs(logs || []);
      }
      
      return logs || [];
    } catch (error) {
      console.error('❌ Failed to load recent logs:', error);
      if (!append) {
        setRecentLogs([]);
      }
      return [];
    } finally {
      setLogsLoading(false);
    }
  }, [user]);

  const refreshData = useCallback(async (startDate?: string, endDate?: string) => {
    setDataLoaded(false);
    await Promise.all([
      loadUsageData(startDate, endDate),
      loadRecentLogs(startDate, endDate)
    ]);
    setDataLoaded(true);
  }, [loadUsageData, loadRecentLogs]);

  const refreshCurrentMonthData = useCallback(async () => {
    if (!user) return;

    try {
      // Get current month's usage logs for cost calculation
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const endOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
      
      const usageLogs = await getUserUsageLogs(
        user.id, 
        10000, // Get all logs for the month
        0,
        startOfMonth.toISOString(),
        endOfMonth.toISOString()
      );

      if (usageLogs && usageLogs.length > 0) {
        // Sort by creation date to get chronological order
        const sortedLogs = usageLogs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        
        // Only count costs for requests beyond the first FREE_REQUESTS_PER_MONTH
        const billableLogs = sortedLogs.slice(FREE_REQUESTS_PER_MONTH);
        const billableCost = billableLogs.reduce((sum, log) => sum + (log.cost_usd || 0), 0);
        
        const totalRequests = usageLogs.length;
        const freeRequestsUsed = Math.min(totalRequests, FREE_REQUESTS_PER_MONTH);
        const freeRequestsRemaining = Math.max(0, FREE_REQUESTS_PER_MONTH - freeRequestsUsed);
        
        // Calculate average calls per day for current month
        const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
        const avgCallsPerDay = totalRequests / daysInMonth;
        
        setCurrentMonthUsage({
          requests: totalRequests,
          cost: billableCost,
          freeRequestsUsed,
          freeRequestsRemaining,
          avgCallsPerDay
        });
      } else {
        setCurrentMonthUsage({
          requests: 0,
          cost: 0,
          freeRequestsUsed: 0,
          freeRequestsRemaining: FREE_REQUESTS_PER_MONTH,
          avgCallsPerDay: 0
        });
      }
    } catch (error) {
      console.error('Failed to calculate current month usage:', error);
      setCurrentMonthUsage({
        requests: 0,
        cost: 0,
        freeRequestsUsed: 0,
        freeRequestsRemaining: FREE_REQUESTS_PER_MONTH,
        avgCallsPerDay: 0
      });
    }
  }, [user]);

  // Load data only once when user is available and data hasn't been loaded yet
  useEffect(() => {
    if (user && !dataLoaded) {
      Promise.all([
        refreshData(),
        refreshCurrentMonthData()
      ]).then(() => {
        setDataLoaded(true);
      });
    }
  }, [user, dataLoaded, refreshData, refreshCurrentMonthData]);

  const value: UsageDataContextType = {
    usageData,
    recentLogs,
    currentMonthUsage,
    loading,
    logsLoading,
    dataLoaded,
    loadUsageData,
    loadRecentLogs,
    refreshData,
    refreshCurrentMonthData
  };

  return (
    <UsageDataContext.Provider value={value}>
      {children}
    </UsageDataContext.Provider>
  );
};
